import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegisterPage({ onLogin }: any) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'bedrift',
    company_name: '',
    phone: '',
    invitation_code: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success(t('registerPage.kontoOpprettetLoggInnFor', 'Konto opprettet! Logg inn for å aktivere tofaktorautentisering.'));
      navigate('/login');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('registerPage.registreringFeilet', 'Registrering feilet'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-2xl mb-4 shadow-lg">
            <Bus className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: '"Manrope", sans-serif' }}>{t('registerPage.traveleaseAi', 'TravelEase.AI')}</h1>
          <p className="text-muted-foreground">{t('registerPage.opprettEnKonto', 'Opprett en konto')}</p>
        </div>

        <Card data-testid="register-form">
          <CardHeader>
            <CardTitle>{t('registerPage.registrerDeg', 'Registrer deg')}</CardTitle>
            <CardDescription>{t('registerPage.fyllUtSkjemaetForA', 'Fyll ut skjemaet for å opprette en konto')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t('registerPage.ePost', 'E-post')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPH2')}
                  value={formData.email}
                  onChange={(e: any) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password">{t('registerPage.passord', 'Passord')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e: any) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="role">{t('registerPage.brukertype', 'Brukertype')}</Label>
                <Select value={formData.role} onValueChange={(value: any) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="mt-1" data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bedrift">{t('registerPage.bedriftskunde', 'Bedriftskunde')}</SelectItem>
                    <SelectItem value="enkeltperson">{t('registerPage.privatperson', 'Privatperson')}</SelectItem>
                    <SelectItem value="sjåfør">{t('auto.sjfr_233', 'Driver')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.role === 'bedrift' && (
                <div>
                  <Label htmlFor="company_name">{t('registerPage.firmanavn', 'Firmanavn')}</Label>
                  <Input
                    id="company_name"
                    type="text"
                    placeholder={t('auth.companyNamePH')}
                    value={formData.company_name}
                    onChange={(e: any) => setFormData({ ...formData, company_name: e.target.value })}
                    required={formData.role === 'bedrift'}
                    data-testid="company-input"
                    className="mt-1"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="phone">{t('registerPage.telefonnummerValgfritt', 'Telefonnummer (valgfritt)')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+47 123 45 678"
                  value={formData.phone}
                  onChange={(e: any) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="phone-input"
                  className="mt-1"
                />
              </div>
              {formData.role === 'bedrift' && (
                <div>
                  <Label htmlFor="invitation_code">{t('registerPage.invitasjonskodeValgfritt', 'Invitasjonskode (valgfritt)')}</Label>
                  <Input
                    id="invitation_code"
                    type="text"
                    placeholder={t('auth.inviteCode', 'ABC12345')}
                    value={formData.invitation_code}
                    onChange={(e: any) => setFormData({ ...formData, invitation_code: e.target.value.toUpperCase() })}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('registerPage.invitasjonskodeHint', 'Har du fått en invitasjonskode? Bruk den for å bli med i et eksisterende selskap')}
                  </p>
                </div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="register-button"
              >
                {loading ? 'Oppretter konto…' : 'Registrer deg'}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">{t('registerPage.harDuAlleredeKonto', 'Har du allerede konto?')}</span>{' '}
              <Link to="/login" className="text-primary hover:text-primary/80 font-medium" data-testid="login-link">
                {t('registerPage.loggInn', 'Logg inn')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
